import React, { useEffect, useState } from 'react';
import { Button, Copy, Heading, Stack } from '@grubhubprod/gh-cookbook-react';
import { Column, Flex, Row, Square } from '@grubhubprod/gh-cookbook-react/dist';
import {
  deleteInvoiceContact,
  getInvoiceContacts,
  IInvoiceContact,
  SubtotalInvoiceMethod,
} from 'src/utilities/invoiceContact.utils';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import { EnterpriseInvoiceRecipientForm } from 'src/components/enterprise/EnterpriseInvoiceRecipients/EnterpriseInvoiceRecipientForm';

export const InvoiceTypeMap = {
  [SubtotalInvoiceMethod.MEAL_TYPE]: 'company',
  [SubtotalInvoiceMethod.LOCATION]: 'location',
  [SubtotalInvoiceMethod.DEPARTMENT]: 'department',
};

export interface EnterpriseManageInvoiceRecipientsProps {
  corpId: string;
  subtotalMethod: SubtotalInvoiceMethod;
  resourceId?: string;
}

export function EnterpriseManageInvoiceRecipients({
  corpId,
  subtotalMethod,
  resourceId,
}: EnterpriseManageInvoiceRecipientsProps): JSX.Element {
  const [invoiceRecipients, setInvoiceRecipients] = useState<IInvoiceContact[]>(
    []
  );
  const [deletingState, setDeletingState] = useState({
    isInProgress: false,
    idBeingDeleted: null,
  });

  useEffect(() => {
    if (corpId && subtotalMethod) {
      getInvoiceRecipients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId, subtotalMethod, resourceId]);

  async function getInvoiceRecipients() {
    try {
      const invoiceContacts = await getInvoiceContacts({
        corpId,
        subtotalMethod,
        resourceId,
      });

      setInvoiceRecipients(invoiceContacts);
    } catch (error) {
      pushErrorFromAPI(error, {
        fallbackMessage:
          'Unable to retrieve invoice recipients. Please try again.',
      });
    }
  }

  async function deleteInvoiceRecipients(recipient: IInvoiceContact) {
    setDeletingState({
      ...deletingState,
      isInProgress: true,
    });
    const fullContactName = `${recipient.first_name} ${recipient.last_name}`;

    try {
      await deleteInvoiceContact({
        corpId,
        subtotalMethod,
        resourceId,
        invoiceContactId: recipient.invoice_contact_id,
      });
      pushSuccess(`${fullContactName} was removed`);
      getInvoiceRecipients();
    } catch (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: `${fullContactName} was not removed. Try again.`,
      });
    } finally {
      resetDeletingState();
    }
  }
  function resetDeletingState() {
    setDeletingState({
      idBeingDeleted: null,
      isInProgress: false,
    });
  }

  return (
    <Stack>
      <Stack y="xx-sm">
        <Heading variant="h4">Add a new invoice recipient</Heading>
      </Stack>
      <Stack y="md">
        <Copy variant="body" color="primaryText">
          The following recipients will be emailed all invoice-related emails
          for this {InvoiceTypeMap[subtotalMethod]}.
        </Copy>
      </Stack>
      <Stack y="lg">
        <EnterpriseInvoiceRecipientForm
          corpId={corpId}
          resourceId={resourceId}
          subtotalMethod={subtotalMethod}
          onSubmitted={getInvoiceRecipients}
        />
      </Stack>
      <Stack y="x-sm">
        <Heading variant="h6">Recipients of invoices</Heading>
      </Stack>
      <Stack y="x-sm">
        {invoiceRecipients.map((recipient, index) => (
          <Row gutter={12} key={recipient.invoice_contact_id}>
            <Column gutter={12} phone={6}>
              <Square
                inset="sm"
                style={{
                  paddingLeft: 0,
                }}
                borderBottom={
                  index === invoiceRecipients.length - 1 ? 'none' : '1px solid'
                }
                borderColor="lightLine"
              >
                <Flex justifyContent="space-between">
                  <Flex flexDirection="column">
                    {`${recipient.first_name} ${recipient.last_name}`}
                    <Copy variant="body" color="secondaryText">
                      {recipient.email}
                    </Copy>
                    <Copy variant="body" color="secondaryText">
                      {recipient.phone}
                    </Copy>
                  </Flex>
                  <Flex
                    alignItems="center"
                    flexBasis="50%"
                    justifyContent="end"
                  >
                    {recipient.primary ? (
                      <Copy
                        variant="caption"
                        color="secondaryText"
                        textAlign="end"
                      >
                        To change or remove a primary contact, reach out to your
                        Client Success Manager.
                      </Copy>
                    ) : (
                      <Button
                        buttonSize="medium"
                        variant="tertiary"
                        disabled={
                          deletingState.idBeingDeleted ===
                          recipient.invoice_contact_id
                        }
                        onClick={() =>
                          setDeletingState({
                            idBeingDeleted: recipient.invoice_contact_id,
                            isInProgress: false,
                          })
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Flex>
              </Square>
            </Column>
            <Column gutter={12} phone={6}>
              {deletingState.idBeingDeleted ===
                recipient.invoice_contact_id && (
                <Flex alignItems="center" height="100%">
                  <Stack x="x-sm">
                    <Copy variant="body">Remove invoice recipient?</Copy>
                  </Stack>
                  <Stack x="x-sm">
                    <Button
                      variant="primary"
                      isLoading={deletingState.isInProgress}
                      disabled={deletingState.isInProgress}
                      onClick={() => deleteInvoiceRecipients(recipient)}
                    >
                      Yes
                    </Button>
                  </Stack>
                  <Button
                    disabled={deletingState.isInProgress}
                    variant="secondary"
                    onClick={resetDeletingState}
                  >
                    No
                  </Button>
                </Flex>
              )}
            </Column>
          </Row>
        ))}
      </Stack>
    </Stack>
  );
}
